/**
 * WashWax Dealer Quote Builder
 * ─────────────────────────────
 * - Reads catalog from injected JSON (#dq-catalog-data)
 * - Per-unit half-up rounding to match QuickBooks/PDF
 * - localStorage persistence per visit
 * - Dealer code soft gate
 * - Submits via Shopify contact form (window.location to /contact submission)
 */
(function () {
  'use strict';

  const root = document.querySelector('.dq-root');
  if (!root) return;

  // ────────────────────────────────────────────────────────
  // Load injected catalog
  // ────────────────────────────────────────────────────────
  let CATALOG;
  try {
    const dataEl = document.getElementById('dq-catalog-data');
    CATALOG = JSON.parse(dataEl.textContent);
  } catch (err) {
    console.error('[dq] Failed to parse catalog data', err);
    return;
  }

  const SHOW_SKU = root.dataset.showSku !== 'false';
  const SHOW_UPC = root.dataset.showUpc === 'true';
  const DEALER_CODE = (root.dataset.dealerCode || '').trim().toUpperCase();
  const GATE_ENABLED = root.dataset.gateEnabled === 'true';
  const SUBMIT_EMAIL = root.dataset.submitEmail || '';
  const KLAVIYO_PUBLIC_KEY = (root.dataset.klaviyoKey || '').trim();
  const KLAVIYO_LIST_ID = (root.dataset.klaviyoListId || '').trim();
  const STORAGE_KEY = 'ww-dealer-quote-v1';
  const GATE_STORAGE_KEY = 'ww-dealer-gate-passed';
  const IGNORE_CAPS = root.dataset.ignoreCaps === 'true';
  const MIN_ORDER = parseFloat(root.dataset.minOrder) || 0;
  

  // ────────────────────────────────────────────────────────
  // State
  // ────────────────────────────────────────────────────────
  let cart = new Map();
  let activeCategory = 'all';
  let searchTerm = '';

  // ────────────────────────────────────────────────────────
  // Math: half-up rounding matches Excel/QuickBooks
  // ────────────────────────────────────────────────────────
  function roundHalfUp(value) {
    return Math.sign(value) * Math.round(Math.abs(value) * 100 + 1e-9) / 100;
  }

  function unitDealerPrice(map, effectiveDiscount) {
    return roundHalfUp(map * (1 - effectiveDiscount));
  }

  function getTier(qualifyingSubtotal) {
    for (const t of CATALOG.tiers) {
      if (qualifyingSubtotal >= t.min && (t.max === null || qualifyingSubtotal <= t.max)) {
        return t;
      }
    }
    return CATALOG.tiers[0];
  }

  function getNextTier(currentDiscount) {
    return CATALOG.tiers.find(t => t.discount > currentDiscount) || null;
  }

  function calculateQuote() {
    const skuMap = Object.fromEntries(CATALOG.products.map(p => [p.sku, p]));
    let qualifyingSubtotal = 0;
    let mapSubtotal = 0;
    const lines = [];

    for (const [sku, qty] of cart) {
      if (qty <= 0) continue;
      const p = skuMap[sku];
      if (!p) continue;
      const lineMap = roundHalfUp(p.map * qty);
      mapSubtotal += lineMap;
      if (p.tier_eligible) qualifyingSubtotal += lineMap;
      lines.push({ product: p, qty, lineMap });
    }
    mapSubtotal = roundHalfUp(mapSubtotal);
    qualifyingSubtotal = roundHalfUp(qualifyingSubtotal);

    const tier = getTier(qualifyingSubtotal);
    const meetsMin = MIN_ORDER <= 0 || mapSubtotal >= MIN_ORDER;
    let grandTotal = 0;
    for (const line of lines) {
      const tierDiscount = meetsMin ? tier.discount : 0;
      const effective = IGNORE_CAPS ? tierDiscount : Math.min(tierDiscount, line.product.max_discount);
      line.effectiveDiscount = effective;
      line.unitDealer = unitDealerPrice(line.product.map, effective);
      line.lineTotal = roundHalfUp(line.unitDealer * line.qty);
      grandTotal += line.lineTotal;
    }
    grandTotal = roundHalfUp(grandTotal);

    return {
      qualifyingSubtotal, mapSubtotal, grandTotal, meetsMin,
      savings: roundHalfUp(mapSubtotal - grandTotal),
      tier, nextTier: getNextTier(tier.discount), lines
    };
  }

  // ────────────────────────────────────────────────────────
  // Formatters
  // ────────────────────────────────────────────────────────
  const fmtMoney = n => '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtPct = n => Math.round(n * 100) + '%';

  function displayName(p) {
    if (p.category !== 'liquids') return p.name;
    if (!p.size || p.size === 'N/A') return p.name;
    if (p.name.toLowerCase().includes(p.size.toLowerCase())) return p.name;
    return `${p.name} – ${p.size}`;
  }

// ────────────────────────────────────────────────────────
  // Tier price grid (reference pricing per discount tier)
  // ────────────────────────────────────────────────────────
const TIER_COLS = (CATALOG.tiers || [])
    .filter(t => t.discount > 0)
    .sort((a, b) => a.discount - b.discount);

  const SKU_INDEX = Object.fromEntries(CATALOG.products.map(p => [p.sku, p]));


    function tierFloorInfo(p) {
    const cap = IGNORE_CAPS ? 1 : ((typeof p.max_discount === 'number' && p.max_discount > 0) ? p.max_discount : 0.35);
    // First tier column that reaches the cap = the dealer's true floor price
    const capIndex = TIER_COLS.findIndex(t => t.discount >= cap - 1e-9);
    return { cap, capIndex };
  }

function tierCellsHtml(p) {
    const { cap, capIndex } = tierFloorInfo(p);
    return TIER_COLS.map((t, i) => {
      if (capIndex !== -1 && i > capIndex) {
        return `<div class="dq-tier-cell na" data-ti="${i}"><span class="dq-tier-amt">N/A</span></div>`;
      }
      const price = roundHalfUp(p.map * (1 - Math.min(t.discount, cap)));
      return `<div class="dq-tier-cell" data-ti="${i}"><span class="dq-tier-amt">${fmtMoney(price)}</span></div>`;
    }).join('');
  }

  function tierChipsHtml(p) {
    const { cap, capIndex } = tierFloorInfo(p);
    let chips = `<span class="dq-tier-chip retail">MAP <strong>${fmtMoney(p.map)}</strong></span>`;
    chips += TIER_COLS.map((t, i) => {
      if (capIndex !== -1 && i > capIndex) {
        return `<span class="dq-tier-chip na" data-ti="${i}">${fmtPct(t.discount)} <strong>N/A</strong></span>`;
      }
      const price = roundHalfUp(p.map * (1 - Math.min(t.discount, cap)));
      return `<span class="dq-tier-chip" data-ti="${i}">${fmtPct(t.discount)} <strong>${fmtMoney(price)}</strong></span>`;
    }).join('');
    return chips;
  }
  function effectiveTierIndex(p, currentDiscount) {
    if (!currentDiscount || currentDiscount <= 0) return -1;
    const { cap } = tierFloorInfo(p);
    const eff = Math.min(currentDiscount, cap);           // capped per product
    return TIER_COLS.findIndex(t => Math.abs(t.discount - eff) < 1e-9);
  }

  function updateTierHighlight(currentDiscount) {
    if (currentDiscount === undefined) currentDiscount = calculateQuote().tier.discount;
    document.querySelectorAll('.dq-product-row').forEach(row => {
      const p = SKU_INDEX[row.dataset.sku];
      if (!p) return;
      const activeIdx = effectiveTierIndex(p, currentDiscount);
      row.querySelectorAll('.dq-tier-cell[data-ti], .dq-tier-chip[data-ti]').forEach(el => {
        el.classList.toggle('active-tier', Number(el.dataset.ti) === activeIdx);
      });
    });
  }
function colheadHtml() {
  let h = `<div class="dq-colhead">
    <span></span>
    <span class="dq-colhead-item">Item</span>
    <span class="dq-colhead-r">Retail</span>`;
  h += TIER_COLS.map(t => `<span class="dq-colhead-r">${fmtPct(t.discount)}</span>`).join('');
  h += `<span class="dq-colhead-r">Qty</span></div>`;
  return h;
}

  // ────────────────────────────────────────────────────────
  // Persistence
  // ────────────────────────────────────────────────────────
  function saveCart() {
    try {
      const obj = Object.fromEntries(cart);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
    } catch (e) { /* localStorage may be unavailable */ }
  }
  function loadCart() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const obj = JSON.parse(raw);
      cart = new Map(Object.entries(obj).map(([k, v]) => [k, Number(v)]));
    } catch (e) { cart = new Map(); }
  }
  function clearCart() {
    cart.clear();
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
  }

  // ────────────────────────────────────────────────────────
  // Render: catalog
  // ────────────────────────────────────────────────────────
  function renderCatalog() {
    const container = document.getElementById('dq-catalog');
    if (!container) return;
    if (CATALOG.products.length === 0) return; // Liquid already showed empty state

    // Reveal the "Other" filter tab only if uncategorized products exist
    const otherBtn = root.querySelector('.dq-filter-other');
    if (otherBtn) {
      const hasUncategorized = CATALOG.products.some(p => p.category === 'uncategorized');
      otherBtn.style.display = hasUncategorized ? '' : 'none';
    }

    let filtered = CATALOG.products;
    if (activeCategory !== 'all') {
      filtered = filtered.filter(p => p.category === activeCategory);
    }
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(q) ||
        p.sku.toLowerCase().includes(q) ||
        (p.size && p.size.toLowerCase().includes(q)) ||
        (p.group && p.group.toLowerCase().includes(q))
      );
    }

    // Group: category > group
    const byCategory = {};
    for (const p of filtered) {
      const cat = p.category || 'uncategorized';
      if (!byCategory[cat]) byCategory[cat] = {};
      const grp = p.group || p.name;
      if (!byCategory[cat][grp]) byCategory[cat][grp] = [];
      byCategory[cat][grp].push(p);
    }

    let html = '';
    for (const cat of CATALOG.categories) {
      if (!byCategory[cat.id]) continue;
      html += `<div class="dq-category-section">
        <div class="dq-category-header">
          <span class="dq-category-name">${escapeHtml(cat.name)}</span>
          <span class="dq-category-desc">${escapeHtml(cat.description)}</span>
        </div>`;
      for (const [groupName, items] of Object.entries(byCategory[cat.id])) {
        html += `<div class="dq-group-block">
          <div class="dq-group-header dq-group-header--cols">
            <span>${escapeHtml(groupName)}</span>
            ${colheadHtml()}
          </div>`;
        for (const p of items) {
          const qty = cart.get(p.sku) || 0;
          const unavailable = p.available === false;
          const imgHtml = p.image
            ? `<button class="dq-product-thumb-btn" data-lightbox-src="${escapeAttr(p.image)}" data-lightbox-caption="${escapeAttr(p.name)}" aria-label="View ${escapeAttr(p.name)}">
                <img class="dq-product-thumb" src="${escapeAttr(p.image)}" alt="" loading="lazy">
               </button>`
            : `<div class="dq-product-thumb dq-product-thumb-empty" aria-hidden="true"></div>`;
          html += `<div class="dq-product-row${qty > 0 ? ' has-qty' : ''}${unavailable ? ' unavailable' : ''}" data-sku="${escapeAttr(p.sku)}">
            ${imgHtml}
            <div class="dq-product-info">
              ${SHOW_SKU ? `<div class="dq-product-sku">${escapeHtml(p.sku)}</div>` : ''}
              <div class="dq-product-name">${escapeHtml(displayName(p))}${unavailable ? ' · <em>Out of stock</em>' : ''}</div>
              ${SHOW_UPC && p.upc ? `<div class="dq-product-upc">UPC: ${escapeHtml(p.upc)}</div>` : ''}
              <div class="dq-tier-chips">${tierChipsHtml(p)}</div>
            </div>
            <div class="dq-product-map">${fmtMoney(p.map)}</div>
            ${tierCellsHtml(p)}
            <div class="dq-qty-control">
              <button class="dq-qty-btn" data-action="dec" data-sku="${escapeAttr(p.sku)}"${qty <= 0 ? ' disabled' : ''} aria-label="Decrease">−</button>
              <input type="number" class="dq-qty-input" data-sku="${escapeAttr(p.sku)}" value="${qty}" min="0" max="9999" inputmode="numeric" aria-label="Quantity for ${escapeAttr(p.sku)}">
              <button class="dq-qty-btn" data-action="inc" data-sku="${escapeAttr(p.sku)}" aria-label="Increase">+</button>
            </div>
          </div>`;
        }
        html += `</div>`;
      }
      html += `</div>`;
    }

    if (!html) {
      html = `<div class="dq-empty-catalog"><p>No products match your filter.</p></div>`;
    }

  container.innerHTML = html;
    updateTierHighlight();
  }

  // ────────────────────────────────────────────────────────
  // Render: quote panel
  // ────────────────────────────────────────────────────────
function renderQuote() {
    const q = calculateQuote();
    const $ = id => document.getElementById(id);

    // ── Effective tier display ──
    // If all cart items are capped below the unlocked tier,
    // show what they're actually receiving, not what was unlocked.
    let displayLabel = q.tier.label;
    let progressPct = 0;
    let nextMarkText = '✓ Max tier unlocked';

    if (q.lines.length > 0) {
      const maxEffective = Math.max(...q.lines.map(l => l.effectiveDiscount));
      if (maxEffective < q.tier.discount) {
        const capTier = CATALOG.tiers.find(t => Math.abs(t.discount - maxEffective) < 1e-9) || CATALOG.tiers[0];
        const nextAboveCap = CATALOG.tiers.find(t => t.discount > maxEffective) || null;
        displayLabel = capTier.label + ' (cap)';
        progressPct = nextAboveCap
          ? Math.min(100, (q.qualifyingSubtotal / nextAboveCap.min) * 100)
          : 100;
        nextMarkText = nextAboveCap
          ? `→ Add uncapped products for ${fmtPct(nextAboveCap.discount)} off`
          : '✓ Max tier unlocked';
      } else {
        if (q.nextTier) {
          progressPct = Math.min(100, (q.qualifyingSubtotal / q.nextTier.min) * 100);
          nextMarkText = `→ ${fmtMoney(q.nextTier.min)} = ${fmtPct(q.nextTier.discount)} off`;
        } else {
          progressPct = 100;
        }
      }
    } else {
      if (q.nextTier) {
        progressPct = Math.min(100, (q.qualifyingSubtotal / q.nextTier.min) * 100);
        nextMarkText = `→ ${fmtMoney(q.nextTier.min)} = ${fmtPct(q.nextTier.discount)} off`;
      } else {
        progressPct = 100;
      }
    }

    // Minimum-order unlock display (only when a minimum is configured)
    if (MIN_ORDER > 0 && !q.meetsMin) {
      displayLabel = q.lines.length > 0 ? 'Discount locked' : q.tier.label;
      progressPct = Math.min(100, (q.mapSubtotal / MIN_ORDER) * 100);
      nextMarkText = `→ ${fmtMoney(MIN_ORDER)} order unlocks ${fmtPct(q.tier.discount)} off`;
    }

    $('dq-tier-name').textContent = displayLabel;
    $('dq-qualifying').textContent = fmtMoney(q.qualifyingSubtotal) + ' qualifying';
    $('dq-progress-bar').style.width = progressPct + '%';
    $('dq-next-mark').textContent = nextMarkText;

    const hint = $('dq-progress-hint');
    if (q.nextTier && q.qualifyingSubtotal > 0) {
      const gap = roundHalfUp(q.nextTier.min - q.qualifyingSubtotal);
      hint.textContent = `Add ${fmtMoney(gap)} in liquids to unlock ${fmtPct(q.nextTier.discount)} off`;
      hint.style.display = 'block';
    } else {
      hint.style.display = 'none';
    }

    // Tier pills
    document.querySelectorAll('.dq-tier-pill').forEach(pill => {
      pill.classList.toggle('active', Math.abs(parseFloat(pill.dataset.tier) - q.tier.discount) < 0.001);
    });

    // Line items
    const body = $('dq-quote-body');
    if (q.lines.length === 0) {
      body.innerHTML = `<div class="dq-quote-empty">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/></svg>
        <div>Enter quantities on the left to build your quote.</div>
      </div>`;
    } else {
      let html = '';
      for (const line of q.lines) {
        const capped = line.effectiveDiscount < q.tier.discount && q.tier.discount > 0;
        const noDisc = line.effectiveDiscount === 0;
        const tagClass = noDisc ? 'none' : capped ? 'capped' : '';
        const tagText = noDisc ? 'no disc.' : capped ? `${fmtPct(line.effectiveDiscount)} capped` : `${fmtPct(line.effectiveDiscount)}`;
        html += `<div class="dq-quote-line">
          <div class="dq-quote-line-info">
            <div class="dq-quote-line-name">${escapeHtml(displayName(line.product))}</div>
            <div class="dq-quote-line-meta">
              ${escapeHtml(line.product.size || '')} · qty ${line.qty}
              <span class="dq-disc-tag ${tagClass}">${tagText}</span>
            </div>
          </div>
          <div class="dq-quote-line-price">
            ${line.effectiveDiscount > 0 ? `<div class="dq-map-strike">${fmtMoney(line.lineMap)}</div>` : ''}
            <div class="dq-disc-price">${fmtMoney(line.lineTotal)}</div>
            ${line.qty > 1 ? `<div class="dq-unit-price">${fmtMoney(line.unitDealer)} ea</div>` : ''}
          </div>
        </div>`;
      }
      body.innerHTML = html;
    }

    $('dq-map-subtotal').textContent = fmtMoney(q.mapSubtotal);
    $('dq-savings').textContent = fmtMoney(q.savings);
    $('dq-grand-total').textContent = fmtMoney(q.grandTotal);
    // Mobile total bar
const mobileBar = document.getElementById('dq-mobile-total-bar');
const mobileAmt = document.getElementById('dq-mobile-total-amount');
const mobileTier = document.getElementById('dq-mobile-total-tier');
if (mobileBar) {
  mobileBar.style.display = q.lines.length > 0 ? '' : 'none';
  if (mobileAmt) mobileAmt.textContent = fmtMoney(q.grandTotal);
  if (mobileTier) mobileTier.textContent = displayLabel;
}



    const belowMin = MIN_ORDER > 0 && q.mapSubtotal < MIN_ORDER;
    const minNotice = document.getElementById('dq-min-notice');
    if (minNotice) {
      if (q.lines.length > 0 && belowMin) {
        minNotice.textContent = `Add ${fmtMoney(MIN_ORDER - q.mapSubtotal)} more to reach the ${fmtMoney(MIN_ORDER)} minimum and unlock ${fmtPct(q.tier.discount)} off.`;
        minNotice.style.display = '';
      } else {
        minNotice.style.display = 'none';
      }
    }
    $('dq-submit-btn').disabled = q.lines.length === 0 || belowMin;
    updateTierHighlight(q.meetsMin ? q.tier.discount : 0);
  }

  // ────────────────────────────────────────────────────────
  // Cart ops
  // ────────────────────────────────────────────────────────
  function setQty(sku, qty) {
    qty = Math.max(0, Math.min(9999, parseInt(qty) || 0));
    if (qty === 0) cart.delete(sku);
    else cart.set(sku, qty);

    const row = document.querySelector(`.dq-product-row[data-sku="${cssEscape(sku)}"]`);
    if (row) {
      row.classList.toggle('has-qty', qty > 0);
      const input = row.querySelector('.dq-qty-input');
      if (input && input.value !== String(qty)) input.value = qty;
      const decBtn = row.querySelector('[data-action="dec"]');
      if (decBtn) decBtn.disabled = qty <= 0;
    }
    saveCart();
    renderQuote();
  }

  // ────────────────────────────────────────────────────────
  // Dealer gate
  // ────────────────────────────────────────────────────────
  function isGatePassed() {
    if (!GATE_ENABLED) return true;
    try {
      return sessionStorage.getItem(GATE_STORAGE_KEY) === DEALER_CODE;
    } catch (e) { return false; }
  }
  function passGate() {
    try { sessionStorage.setItem(GATE_STORAGE_KEY, DEALER_CODE); } catch (e) {}
    showMainUI();
  }
  function showMainUI() {
    const gate = document.getElementById('dq-gate');
    if (gate) gate.style.display = 'none';
    root.style.setProperty('--dq-tier-count', TIER_COLS.length);
    document.getElementById('dq-tier-ladder').style.display = '';
    document.getElementById('dq-layout').style.display = '';
    const intro = document.getElementById('dq-intro');
    if (intro) intro.style.display = '';
    renderCatalog();
    renderQuote();
  }

  // ────────────────────────────────────────────────────────
  // Submit
  // ────────────────────────────────────────────────────────
  function buildQuoteRef() {
    const d = new Date();
    const ymd = `${String(d.getFullYear()).slice(2)}${String(d.getMonth()+1).padStart(2,'0')}${String(d.getDate()).padStart(2,'0')}`;
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // no confusing chars
    let rand = '';
    for (let i = 0; i < 4; i++) rand += chars[Math.floor(Math.random()*chars.length)];
    return `WW-${ymd}-${rand}`;
  }

  function openSubmitModal() {
    const q = calculateQuote();
    if (q.lines.length === 0) return;
    if (MIN_ORDER > 0 && q.mapSubtotal < MIN_ORDER) return;

    const summary = document.getElementById('dq-modal-summary');
    let html = `<div class="dq-summary-title">Quote Summary — ${q.tier.label}</div>`;
    html += `<table class="dq-summary-table"><thead><tr><th>SKU</th><th>Item</th><th>Qty</th><th>Unit</th><th>Line Total</th></tr></thead><tbody>`;
    for (const line of q.lines) {
      html += `<tr>
        <td>${escapeHtml(line.product.sku)}</td>
        <td>${escapeHtml(displayName(line.product))}</td>
        <td>${line.qty}</td>
        <td>${fmtMoney(line.unitDealer)}</td>
        <td>${fmtMoney(line.lineTotal)}</td>
      </tr>`;
    }
    html += `</tbody><tfoot>
      <tr><td colspan="4">MAP Subtotal</td><td>${fmtMoney(q.mapSubtotal)}</td></tr>
      <tr><td colspan="4">Savings</td><td>${fmtMoney(q.savings)}</td></tr>
      <tr class="grand"><td colspan="4">Total</td><td>${fmtMoney(q.grandTotal)}</td></tr>
    </tfoot></table>`;
    summary.innerHTML = html;

    document.getElementById('dq-submit-form').style.display = '';
    document.getElementById('dq-modal-success').style.display = 'none';
    document.getElementById('dq-submit-modal').style.display = '';
  }

  function closeSubmitModal() {
    document.getElementById('dq-submit-modal').style.display = 'none';
  }

  async function submitQuote(formEl) {
    const q = calculateQuote();
    const ref = buildQuoteRef();
    const fd = new FormData(formEl);

    const company = (fd.get('company') || '').trim();
    const contactName = (fd.get('contact_name') || '').trim();
    const email = (fd.get('email') || '').trim();
    const phone = (fd.get('phone') || '').trim();
    const poNumber = (fd.get('po_number') || '').trim();
    const upsAccount = (fd.get('ups_account') || '').trim();
    const shippingAddress = (fd.get('shipping_address') || '').trim();
    const requestedShipDate = (fd.get('requested_ship_date') || '').trim();
    const notes = (fd.get('notes') || '').trim();
    const cert = fd.get('resale_cert');
    const certName = (cert && cert.name) ? cert.name : '';
    const submittedAtLocal = new Date().toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });

    // ──────────────────────────────────────────────────────────
    // Build structured line items for Klaviyo flow templating
    // ──────────────────────────────────────────────────────────
    const linesPayload = q.lines.map(l => ({
      sku: l.product.sku,
      name: displayName(l.product),
      size: l.product.size,
      qty: l.qty,
      map: l.product.map,
      map_formatted: fmtMoney(l.product.map),
      effective_discount: l.effectiveDiscount,
      unit_dealer_price: l.unitDealer,
      unit_formatted: fmtMoney(l.unitDealer),
      line_total: l.lineTotal,
      line_formatted: fmtMoney(l.lineTotal)
    }));

    // ──────────────────────────────────────────────────────────
    // Pre-render line item HTML (dealer-clean and internal-full)
    // We render in JS so Klaviyo just embeds a ready-made block.
    // ──────────────────────────────────────────────────────────
    const linesHtmlDealer = q.lines.map(l => {
      const nm = escapeHtml(displayName(l.product));
      return `<tr>
        <td style="padding:8px 6px;border-bottom:1px solid #e5e7eb;font-family:monospace;font-size:12px;color:#475569;">${escapeHtml(l.product.sku)}</td>
        <td style="padding:8px 6px;border-bottom:1px solid #e5e7eb;font-size:13px;color:#11181f;">${nm}</td>
        <td style="padding:8px 6px;border-bottom:1px solid #e5e7eb;text-align:center;font-variant-numeric:tabular-nums;font-size:13px;">${l.qty}</td>
        <td style="padding:8px 6px;border-bottom:1px solid #e5e7eb;text-align:right;font-variant-numeric:tabular-nums;font-size:13px;">${fmtMoney(l.unitDealer)}</td>
        <td style="padding:8px 6px;border-bottom:1px solid #e5e7eb;text-align:right;font-variant-numeric:tabular-nums;font-size:13px;font-weight:600;">${fmtMoney(l.lineTotal)}</td>
      </tr>`;
    }).join('');

    const linesHtmlInternal = q.lines.map(l => {
      const nm = escapeHtml(displayName(l.product));
      const tag = l.effectiveDiscount === 0
        ? '<span style="background:#94a3b8;color:white;padding:1px 5px;border-radius:2px;font-size:10px;font-weight:700;">no disc</span>'
        : l.effectiveDiscount < q.tier.discount
        ? `<span style="background:#b45309;color:white;padding:1px 5px;border-radius:2px;font-size:10px;font-weight:700;">${Math.round(l.effectiveDiscount * 100)}% capped</span>`
        : `<span style="background:#15803d;color:white;padding:1px 5px;border-radius:2px;font-size:10px;font-weight:700;">${Math.round(l.effectiveDiscount * 100)}%</span>`;
      return `<tr>
        <td style="padding:6px 6px;border-bottom:1px solid #e5e7eb;font-family:monospace;font-size:11px;color:#475569;">${escapeHtml(l.product.sku)}</td>
        <td style="padding:6px 6px;border-bottom:1px solid #e5e7eb;font-size:12px;">${nm}</td>
        <td style="padding:6px 6px;border-bottom:1px solid #e5e7eb;text-align:center;font-variant-numeric:tabular-nums;font-size:12px;">${l.qty}</td>
        <td style="padding:6px 6px;border-bottom:1px solid #e5e7eb;text-align:right;font-variant-numeric:tabular-nums;font-size:12px;color:#94a3b8;text-decoration:line-through;">${fmtMoney(l.product.map)}</td>
        <td style="padding:6px 6px;border-bottom:1px solid #e5e7eb;text-align:center;">${tag}</td>
        <td style="padding:6px 6px;border-bottom:1px solid #e5e7eb;text-align:right;font-variant-numeric:tabular-nums;font-size:12px;">${fmtMoney(l.unitDealer)}</td>
        <td style="padding:6px 6px;border-bottom:1px solid #e5e7eb;text-align:right;font-variant-numeric:tabular-nums;font-size:12px;font-weight:600;">${fmtMoney(l.lineTotal)}</td>
      </tr>`;
    }).join('');

    // ──────────────────────────────────────────────────────────
    // DEALER-FACING email body — clean, professional quote
    // Pasted into a single Klaviyo Text block via {{ event.dealer_email_html|safe }}
    // ──────────────────────────────────────────────────────────
    const dealerHtml = `
<div style="font-family:Helvetica;color:#11181f;max-width:640px;margin:0 auto;">
  <div style="background:#0b4268;color:white;padding:24px;border-bottom:3px solid #d18b00;">
    <div style="font-family:monospace;font-size:11px;letter-spacing:0.2em;color:#aebac4;text-transform:uppercase;margin-bottom:6px;">Quote Confirmation</div>
    <div style="font-size:24px;font-weight:800;letter-spacing:-0.01em;">Thank you for your quote request</div>
    <div style="font-family:monospace;font-size:13px;color:#f5b94a;margin-top:8px;letter-spacing:0.05em;">Reference: ${ref}</div>
  </div>

  <div style="padding:24px;background:#fcfcfa;">
    <p style="margin:0 0 16px;font-size:14px;line-height:1.5;">Hi ${escapeHtml(contactName.split(' ')[0] || 'there')},</p>
    <p style="margin:0 0 20px;font-size:14px;line-height:1.5;">We've received your quote request${company ? ' for ' + escapeHtml(company) : ''}. Our team will review your order and respond within 1 business day with availability confirmation and a freight quote.</p>

    <table style="width:100%;border-collapse:collapse;margin:20px 0;border:1px solid #e5e7eb;">
      <thead>
        <tr style="background:#0b4268;color:white;">
          <th style="padding:10px 6px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:0.05em;font-family:monospace;">SKU</th>
          <th style="padding:10px 6px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:0.05em;font-family:monospace;">Item</th>
          <th style="padding:10px 6px;text-align:center;font-size:11px;text-transform:uppercase;letter-spacing:0.05em;font-family:monospace;">Qty</th>
          <th style="padding:10px 6px;text-align:right;font-size:11px;text-transform:uppercase;letter-spacing:0.05em;font-family:monospace;">Unit</th>
          <th style="padding:10px 6px;text-align:right;font-size:11px;text-transform:uppercase;letter-spacing:0.05em;font-family:monospace;">Line Total</th>
        </tr>
      </thead>
      <tbody>${linesHtmlDealer}</tbody>
      <tfoot>
        <tr>
          <td colspan="4" style="padding:14px 6px 4px;text-align:right;font-size:14px;font-weight:700;color:#0b4268;">Estimated Subtotal</td>
          <td style="padding:14px 6px 4px;text-align:right;font-size:18px;font-weight:800;color:#0b4268;font-variant-numeric:tabular-nums;">${fmtMoney(q.grandTotal)}</td>
        </tr>
      </tfoot>
    </table>

    <div style="background:#faf8f4;border-left:3px solid #d18b00;padding:12px 16px;margin:16px 0;font-size:12px;color:#475569;">
      <strong style="color:#11181f;">Estimate only.</strong> Total excludes applicable taxes and shipping. Freight will be quoted separately based on weight, destination, and your preferred carrier. Final invoice may vary from this estimate.
    </div>

    <h3 style="font-size:13px;text-transform:uppercase;letter-spacing:0.08em;color:#475569;font-family:monospace;margin:24px 0 8px;">Your Information</h3>
    <table style="width:100%;font-size:13px;border-collapse:collapse;">
      <tr><td style="padding:4px 0;color:#475569;width:140px;">Company</td><td style="padding:4px 0;">${escapeHtml(company)}</td></tr>
      <tr><td style="padding:4px 0;color:#475569;">Contact</td><td style="padding:4px 0;">${escapeHtml(contactName)}</td></tr>
      <tr><td style="padding:4px 0;color:#475569;">Phone</td><td style="padding:4px 0;">${escapeHtml(phone)}</td></tr>
      ${poNumber ? `<tr><td style="padding:4px 0;color:#475569;">PO Number</td><td style="padding:4px 0;">${escapeHtml(poNumber)}</td></tr>` : ''}
      ${upsAccount ? `<tr><td style="padding:4px 0;color:#475569;">UPS Account</td><td style="padding:4px 0;font-family:monospace;">${escapeHtml(upsAccount)}</td></tr>` : ''}
      ${requestedShipDate ? `<tr><td style="padding:4px 0;color:#475569;">Requested Ship</td><td style="padding:4px 0;">${escapeHtml(requestedShipDate)}</td></tr>` : ''}
    </table>

    <h3 style="font-size:13px;text-transform:uppercase;letter-spacing:0.08em;color:#475569;font-family:monospace;margin:20px 0 8px;">Shipping Address</h3>
    <div style="font-size:13px;line-height:1.5;white-space:pre-wrap;">${escapeHtml(shippingAddress)}</div>

    ${notes ? `<h3 style="font-size:13px;text-transform:uppercase;letter-spacing:0.08em;color:#475569;font-family:monospace;margin:20px 0 8px;">Notes</h3><div style="font-size:13px;line-height:1.5;white-space:pre-wrap;">${escapeHtml(notes)}</div>` : ''}

    <p style="margin:24px 0 8px;font-size:13px;color:#475569;line-height:1.5;">If anything needs to change, just reply to this email with your reference number (<strong style="font-family:monospace;color:#11181f;">${ref}</strong>).</p>
    <p style="margin:0 0 16px;font-size:13px;color:#475569;line-height:1.5;">Thank you for your business,<br><strong style="color:#11181f;">The Aero Cosmetics / WashWax Team</strong></p>
  </div>

  <div style="background:#06243a;color:#b3bfc9;padding:14px 24px;font-size:11px;font-family:monospace;letter-spacing:0.05em;text-align:center;">
    Aero Cosmetics · Approved by Boeing, Airbus & U.S. Military · Since 1987
  </div>
</div>`.trim();

    // ──────────────────────────────────────────────────────────
    // INTERNAL (sales-team) email body — full operational dump
    // ──────────────────────────────────────────────────────────
    const internalHtml = `
<div style="font-family:Helvetica;color:#11181f;max-width:760px;margin:0 auto;">
  <div style="background:#0b4268;color:white;padding:18px 22px;border-bottom:3px solid #d18b00;">
    <div style="font-family:monospace;font-size:11px;letter-spacing:0.2em;color:#aebac4;text-transform:uppercase;">Internal — Sales Team</div>
    <div style="font-size:20px;font-weight:800;margin-top:4px;">New Dealer Quote · ${escapeHtml(company)}</div>
    <div style="font-family:monospace;font-size:12px;color:#f5b94a;margin-top:6px;">Ref ${ref} · ${escapeHtml(submittedAtLocal)} · Tier: ${q.tier.label} · ${fmtMoney(q.grandTotal)}</div>
  </div>

  <div style="padding:20px 22px;background:#fcfcfa;">
    <table style="width:100%;font-size:13px;border-collapse:collapse;margin-bottom:18px;">
      <tr>
        <td style="vertical-align:top;width:50%;padding-right:20px;">
          <h3 style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;color:#475569;font-family:monospace;margin:0 0 8px;">Dealer</h3>
          <div style="font-size:14px;font-weight:600;margin-bottom:4px;">${escapeHtml(company)}</div>
          <div>${escapeHtml(contactName)}</div>
          <div><a href="mailto:${escapeHtml(email)}" style="color:#0b4268;">${escapeHtml(email)}</a></div>
          <div><a href="tel:${escapeHtml(phone)}" style="color:#0b4268;">${escapeHtml(phone)}</a></div>
        </td>
        <td style="vertical-align:top;width:50%;">
          <h3 style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;color:#475569;font-family:monospace;margin:0 0 8px;">Order Details</h3>
          <table style="font-size:12px;border-collapse:collapse;">
            <tr><td style="padding:2px 8px 2px 0;color:#475569;">PO #:</td><td style="font-family:monospace;">${escapeHtml(poNumber) || '—'}</td></tr>
            <tr><td style="padding:2px 8px 2px 0;color:#475569;">UPS Acct:</td><td style="font-family:monospace;">${escapeHtml(upsAccount) || '—'}</td></tr>
            <tr><td style="padding:2px 8px 2px 0;color:#475569;">Req. Ship:</td><td>${escapeHtml(requestedShipDate) || '—'}</td></tr>
            <tr><td style="padding:2px 8px 2px 0;color:#475569;">Resale Cert:</td><td>${escapeHtml(certName) || '<em style="color:#94a3b8;">not provided</em>'}</td></tr>
          </table>
        </td>
      </tr>
    </table>

    <h3 style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;color:#475569;font-family:monospace;margin:0 0 6px;">Ship To</h3>
    <div style="font-size:13px;line-height:1.5;white-space:pre-wrap;background:#faf8f4;padding:10px 14px;border-left:3px solid #d18b00;margin-bottom:18px;">${escapeHtml(shippingAddress)}</div>

    <h3 style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;color:#475569;font-family:monospace;margin:18px 0 6px;">Quote Detail</h3>
    <table style="width:100%;border-collapse:collapse;border:1px solid #e5e7eb;">
      <thead>
        <tr style="background:#06243a;color:white;">
          <th style="padding:8px 6px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:0.05em;font-family:monospace;">SKU</th>
          <th style="padding:8px 6px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:0.05em;font-family:monospace;">Item</th>
          <th style="padding:8px 6px;text-align:center;font-size:10px;text-transform:uppercase;letter-spacing:0.05em;font-family:monospace;">Qty</th>
          <th style="padding:8px 6px;text-align:right;font-size:10px;text-transform:uppercase;letter-spacing:0.05em;font-family:monospace;">MAP</th>
          <th style="padding:8px 6px;text-align:center;font-size:10px;text-transform:uppercase;letter-spacing:0.05em;font-family:monospace;">Disc</th>
          <th style="padding:8px 6px;text-align:right;font-size:10px;text-transform:uppercase;letter-spacing:0.05em;font-family:monospace;">Unit</th>
          <th style="padding:8px 6px;text-align:right;font-size:10px;text-transform:uppercase;letter-spacing:0.05em;font-family:monospace;">Line</th>
        </tr>
      </thead>
      <tbody>${linesHtmlInternal}</tbody>
      <tfoot>
        <tr>
          <td colspan="6" style="padding:6px;text-align:right;color:#475569;font-size:12px;">MAP Subtotal</td>
          <td style="padding:6px;text-align:right;font-variant-numeric:tabular-nums;font-size:12px;">${fmtMoney(q.mapSubtotal)}</td>
        </tr>
        <tr>
          <td colspan="6" style="padding:2px 6px;text-align:right;color:#15803d;font-size:12px;font-weight:600;">Dealer Savings</td>
          <td style="padding:2px 6px;text-align:right;color:#15803d;font-variant-numeric:tabular-nums;font-size:12px;font-weight:600;">${fmtMoney(q.savings)}</td>
        </tr>
        <tr style="border-top:2px solid #0b4268;">
          <td colspan="6" style="padding:8px 6px;text-align:right;font-size:14px;font-weight:800;color:#0b4268;">GRAND TOTAL</td>
          <td style="padding:8px 6px;text-align:right;font-size:16px;font-weight:800;color:#0b4268;font-variant-numeric:tabular-nums;">${fmtMoney(q.grandTotal)}</td>
        </tr>
      </tfoot>
    </table>
    <div style="font-size:11px;color:#94a3b8;margin-top:6px;font-family:monospace;">Qualifying liquid subtotal: ${fmtMoney(q.qualifyingSubtotal)} · ${q.lines.length} lines · ${q.lines.reduce((a,l) => a + l.qty, 0)} units</div>

    ${notes ? `<h3 style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;color:#475569;font-family:monospace;margin:18px 0 6px;">Dealer Notes</h3><div style="font-size:13px;line-height:1.5;white-space:pre-wrap;background:#fffaef;border-left:3px solid #d18b00;padding:10px 14px;">${escapeHtml(notes)}</div>` : ''}

    <div style="margin-top:24px;padding-top:14px;border-top:1px dashed #e5e7eb;font-size:11px;color:#94a3b8;font-family:monospace;">
      Submitted ${escapeHtml(submittedAtLocal)} · Reply directly to dealer at <a href="mailto:${escapeHtml(email)}" style="color:#0b4268;">${escapeHtml(email)}</a>
    </div>
  </div>
</div>`.trim();

    // ──────────────────────────────────────────────────────────
    // Plaintext fallback body (used by mailto only)
    // ──────────────────────────────────────────────────────────
    let plainBody = `DEALER QUOTE REQUEST\n`;
    plainBody += `Reference: ${ref}\n`;
    plainBody += `Submitted: ${submittedAtLocal}\n\n`;
    plainBody += `COMPANY\n`;
    plainBody += `  Company:     ${company}\n`;
    plainBody += `  Contact:     ${contactName}\n`;
    plainBody += `  Email:       ${email}\n`;
    plainBody += `  Phone:       ${phone}\n`;
    if (poNumber) plainBody += `  PO Number:   ${poNumber}\n`;
    if (upsAccount) plainBody += `  UPS Account: ${upsAccount}\n`;
    if (requestedShipDate) plainBody += `  Ship Date:   ${requestedShipDate}\n`;
    plainBody += `\nSHIPPING ADDRESS\n  ${shippingAddress.replace(/\n/g, '\n  ')}\n\n`;
    plainBody += `QUOTE — Tier: ${q.tier.label}\n`;
    plainBody += `${'SKU'.padEnd(12)}${'Item'.padEnd(46)}${'Qty'.padStart(5)}${'Unit'.padStart(10)}${'Total'.padStart(12)}\n`;
    plainBody += '─'.repeat(85) + '\n';
    for (const line of q.lines) {
      const nm = displayName(line.product);
      plainBody += line.product.sku.padEnd(12);
      plainBody += (nm.length > 44 ? nm.slice(0, 43) + '…' : nm).padEnd(46);
      plainBody += String(line.qty).padStart(5);
      plainBody += fmtMoney(line.unitDealer).padStart(10);
      plainBody += fmtMoney(line.lineTotal).padStart(12);
      plainBody += '\n';
    }
    plainBody += '─'.repeat(85) + '\n';
    plainBody += `${'MAP Subtotal'.padStart(73)}${fmtMoney(q.mapSubtotal).padStart(12)}\n`;
    plainBody += `${'Savings'.padStart(73)}${fmtMoney(q.savings).padStart(12)}\n`;
    plainBody += `${'GRAND TOTAL'.padStart(73)}${fmtMoney(q.grandTotal).padStart(12)}\n\n`;
    if (notes) plainBody += `NOTES\n${notes}\n\n`;
    plainBody += `Resale certificate: ${certName ? 'attached (' + certName + ') — dealer to email separately' : 'not provided'}\n`;

    // ──────────────────────────────────────────────────────────
    // Event properties for Klaviyo
    // Structured for two different email flows to consume.
    // ──────────────────────────────────────────────────────────
    const eventProperties = {
      // Identification
      reference: ref,
      submitted_at: new Date().toISOString(),
      submitted_at_display: submittedAtLocal,

      // Dealer info (used in both emails)
      company,
      contact_name: contactName,
      contact_first_name: contactName.split(' ')[0] || '',
      email,
      phone,
      po_number: poNumber,
      ups_account: upsAccount,
      shipping_address: shippingAddress,
      requested_ship_date: requestedShipDate,
      notes,
      resale_cert_filename: certName,

      // Pricing summary
      tier_label: q.tier.label,
      tier_discount: q.tier.discount,
      qualifying_subtotal: q.qualifyingSubtotal,
      map_subtotal: q.mapSubtotal,
      grand_total: q.grandTotal,
      grand_total_formatted: fmtMoney(q.grandTotal),
      savings: q.savings,
      line_count: q.lines.length,
      item_count: q.lines.reduce((a, l) => a + l.qty, 0),

      // Line items (structured array)
      lines: linesPayload,

      // Pre-rendered email bodies — drop straight into Klaviyo via {{ event.dealer_email_html|safe }}
      dealer_email_html: dealerHtml,
      internal_email_html: internalHtml,

      // Routing
      forward_to: SUBMIT_EMAIL,
      sales_team_email: SUBMIT_EMAIL
    };

    // Try Klaviyo first, fall back to mailto if Klaviyo fails or isn't configured
    let success = false;
    if (KLAVIYO_PUBLIC_KEY && email) {
      try {
        success = await submitToKlaviyo(eventProperties, {
          company, contactName, email, phone, shippingAddress
        });
      } catch (err) {
        console.error('[dq] Klaviyo submit failed, falling back to mailto:', err);
      }
    }

    if (success) {
      // Klaviyo path
      document.getElementById('dq-submit-form').style.display = 'none';
      document.getElementById('dq-modal-success').style.display = '';
      document.getElementById('dq-quote-ref').textContent = ref;
      clearCart();
      renderCatalog();
      renderQuote();
    } else {
      // Mailto fallback — always succeeds at opening the user's mail client
      const subject = `Dealer Quote ${ref} — ${company || 'Unknown'} — ${fmtMoney(q.grandTotal)}`;
      const mailtoUrl = `mailto:${encodeURIComponent(SUBMIT_EMAIL)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(plainBody)}`;
      // Open in new tab so the modal stays visible
      window.open(mailtoUrl, '_blank');
      // Still show success state — the quote is now in their hands to send
      document.getElementById('dq-submit-form').style.display = 'none';
      document.getElementById('dq-modal-success').style.display = '';
      document.getElementById('dq-quote-ref').textContent = ref;
      // Don't clear cart on mailto path — dealer may want to edit before sending email
      showToast('Quote opened in your email client. Send to complete.');
    }
  }

  async function submitToKlaviyo(eventProperties, profile) {
    if (!KLAVIYO_PUBLIC_KEY) return false;

    // Klaviyo Client Events endpoint:
    // POST https://a.klaviyo.com/client/events/?company_id=PUBLIC_KEY
    // Headers: revision, Content-Type
    // No Authorization header on client-side calls — only company_id query param
    const url = `https://a.klaviyo.com/client/events/?company_id=${encodeURIComponent(KLAVIYO_PUBLIC_KEY)}`;

    const payload = {
      data: {
        type: 'event',
        attributes: {
          properties: eventProperties,
          metric: {
            data: {
              type: 'metric',
              attributes: { name: 'Dealer Quote Submitted' }
            }
          },
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: profile.email,
                phone_number: profile.phone || undefined,
                organization: profile.company || undefined,
                first_name: profile.contactName ? profile.contactName.split(' ')[0] : undefined,
                last_name: profile.contactName ? profile.contactName.split(' ').slice(1).join(' ') || undefined : undefined,
                location: profile.shippingAddress ? { address1: profile.shippingAddress.split('\n')[0] || undefined } : undefined,
                properties: {
                  dealer_company: profile.company,
                  dealer_contact_name: profile.contactName,
                  dealer_phone: profile.phone,
                  dealer_shipping_address: profile.shippingAddress,
                  last_quote_ref: eventProperties.reference,
                  last_quote_total: eventProperties.grand_total,
                  last_quote_tier: eventProperties.tier_label
                }
              }
            }
          },
          unique_id: eventProperties.reference
        }
      }
    };

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'revision': '2024-10-15',
        'accept': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    // 202 Accepted = success
    if (res.status === 202) return true;

    // Anything else means failure — log details for debugging
    let errBody = '';
    try { errBody = await res.text(); } catch (e) {}
    console.error(`[dq] Klaviyo returned ${res.status}:`, errBody);
    return false;
  }

  // ────────────────────────────────────────────────────────
  // Utilities
  // ────────────────────────────────────────────────────────
  function showToast(msg) {
    const t = document.getElementById('dq-toast');
    if (!t) return;
    t.textContent = msg;
    t.classList.add('show');
    setTimeout(() => t.classList.remove('show'), 3500);
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }
  function escapeAttr(s) { return escapeHtml(s); }
  function cssEscape(s) {
    if (window.CSS && window.CSS.escape) return window.CSS.escape(s);
    return String(s).replace(/["\\\]\[]/g, '\\$&');
  }

  // ────────────────────────────────────────────────────────
  // Event wiring
  // ────────────────────────────────────────────────────────
  root.addEventListener('click', e => {
    if (e.target.matches('.dq-filter-btn')) {
      root.querySelectorAll('.dq-filter-btn').forEach(b => b.classList.remove('active'));
      e.target.classList.add('active');
      activeCategory = e.target.dataset.cat;
      renderCatalog();
    }
    const thumbBtn = e.target.closest('.dq-product-thumb-btn');
    if (thumbBtn) {
      e.stopPropagation();
      openLightbox(thumbBtn.dataset.lightboxSrc, thumbBtn.dataset.lightboxCaption);
      return;
    }
    if (e.target.matches('.dq-qty-btn')) {
      const sku = e.target.dataset.sku;
      const current = cart.get(sku) || 0;
      setQty(sku, e.target.dataset.action === 'inc' ? current + 1 : current - 1);
    }
    if (e.target.hasAttribute('data-close-modal')) {
      closeSubmitModal();
    }
  });

  root.addEventListener('input', e => {
    if (e.target.matches('.dq-qty-input')) {
      setQty(e.target.dataset.sku, e.target.value);
    }
    if (e.target.id === 'dq-search') {
      searchTerm = e.target.value;
      renderCatalog();
    }
  });

  const clearBtn = document.getElementById('dq-clear-btn');
  if (clearBtn) clearBtn.addEventListener('click', () => {
    if (cart.size === 0) return;
    if (!confirm('Clear all quantities from your quote?')) return;
    clearCart();
    renderCatalog();
    renderQuote();
    showToast('Quote cleared');
  });

  const submitBtn = document.getElementById('dq-submit-btn');
  if (submitBtn) submitBtn.addEventListener('click', openSubmitModal);

  const submitForm = document.getElementById('dq-submit-form');
  if (submitForm) submitForm.addEventListener('submit', e => {
    e.preventDefault();
    submitQuote(submitForm);
  });

  const gateForm = document.getElementById('dq-gate-form');
  if (gateForm) gateForm.addEventListener('submit', e => {
    e.preventDefault();
    const input = document.getElementById('dq-gate-input');
    const err = document.getElementById('dq-gate-error');
    const entered = (input.value || '').trim().toUpperCase();
    if (entered === DEALER_CODE) {
      err.style.display = 'none';
      passGate();
    } else {
      err.textContent = 'Invalid dealer code. Please try again or apply for dealer access.';
      err.style.display = '';
      input.focus();
      input.select();
    }
  });


  // ────────────────────────────────────────────────────────
  // Lightbox
  // ────────────────────────────────────────────────────────
  function buildLightbox() {
    const lb = document.createElement('div');
    lb.id = 'dq-lightbox';
    lb.className = 'dq-lightbox';
    lb.innerHTML = `
      <div class="dq-lightbox-backdrop"></div>
      <div class="dq-lightbox-frame">
        <button class="dq-lightbox-close" aria-label="Close">×</button>
        <img class="dq-lightbox-img" src="" alt="">
        <div class="dq-lightbox-caption"></div>
      </div>`;
    document.body.appendChild(lb);

    lb.querySelector('.dq-lightbox-backdrop').addEventListener('click', closeLightbox);
    lb.querySelector('.dq-lightbox-close').addEventListener('click', closeLightbox);
    document.addEventListener('keydown', e => { if (e.key === 'Escape') closeLightbox(); });
  }

  function openLightbox(imgSrc, caption) {
    const lb = document.getElementById('dq-lightbox');
    if (!lb) return;
    const largeSrc = imgSrc.replace(/width=\d+/, 'width=800');
    const img = lb.querySelector('.dq-lightbox-img');
    const cap = lb.querySelector('.dq-lightbox-caption');
    img.src = '';
    img.src = largeSrc;
    if (cap) cap.textContent = caption || '';
    lb.classList.add('open');
    document.body.style.overflow = 'hidden';
  }

  function closeLightbox() {
    const lb = document.getElementById('dq-lightbox');
    if (!lb) return;
    lb.classList.remove('open');
    document.body.style.overflow = '';
    lb.querySelector('.dq-lightbox-img').src = '';
  }
// ────────────────────────────────────────────────────────
  // Boot
  // ────────────────────────────────────────────────────────
  loadCart();
  buildLightbox();
  if (isGatePassed()) {
    showMainUI();
  }
const mobileTotalBtn = document.getElementById('dq-mobile-total-scroll');
  if (mobileTotalBtn) mobileTotalBtn.addEventListener('click', () => {
    document.getElementById('dq-quote-panel').scrollIntoView({ behavior: 'smooth' });
  });

  // If gate not passed, the gate is already visible from Liquid render.

  // Expose for console debugging
  window.dq = { calculate: calculateQuote, cart, CATALOG, clear: clearCart };
})();